// Empire's ScrollLock (used by the quick shop modal) adds a touchmove listener to
// <body> that calls preventDefault on any touch outside `.allow-scroll-while-locked`.
// Quick shop's add-to-cart opens the cart drawer without ever closing the modal, so
// that listener stays attached and the drawer cannot be scrolled on touch devices.
// Close the modal through its own close button, which is what releases the lock.
function releaseQuickShopScrollLock() {
  const modals = [
    { el: document.querySelector("[data-modal-container]"), visible: "modal-visible", close: "[data-modal-close]" },
    { el: document.querySelector("[data-modal-container-1]"), visible: "modal-1-visible", close: "[data-modal-1-close]" },
  ];

  modals.forEach(({ el, visible, close }) => {
    if (!el) return;

    // Quick shop used to dismiss itself by swapping classes, which left the lock
    // live and the shared modal container permanently display:none.
    el.classList.remove("hidden");

    if (!document.body.classList.contains(visible)) return;

    const closeButton = el.querySelector(close);
    if (closeButton) closeButton.click();
  });

  document.documentElement.classList.remove("scroll-locked");
  document.body.style.top = "";
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.trapFocusHandlers = {};
    this.cartUpdateUnsubscriber = undefined;
    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close()
    );
    this.querySelector("#CartDrawer-Overlay").addEventListener(
      "click",
      this.close.bind(this)
    );
    this.setHeaderCartIconAccessibility();
    this.cartDrawerOpener = document.querySelector(".site-header-cart");
    if (this.cartDrawerOpener) {
      this.cartDrawerOpener.addEventListener("click", (e) => {
        e.preventDefault();
        this.open(this.cartDrawerOpener);
      });
    }
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (event.source === "cart-drawer") {
          return;
        }
        this.onCartUpdate();
      }
    );
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onCartUpdate() {
    return fetch(`${window.location.pathname}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, "text/html");
        const selectors = ["cart-drawer-items", ".cart-drawer__footer"];
        for (const selector of selectors) {
          const targetElement = document.querySelector(selector);
          const sourceElement = html.querySelector(selector);
          if (targetElement && sourceElement) {
            targetElement.replaceWith(sourceElement);
          }
        }

        // Update the cart drawer empty state
        const newCartDrawer = html.querySelector("cart-drawer");
        if (newCartDrawer) {
          const isEmpty = newCartDrawer.classList.contains("is-empty");
          this.classList.toggle("is-empty", isEmpty);
        }
      })
      .catch((e) => {
        console.error("Cart drawer update error:", e);
      });
  }

  removeTrapFocus(elementToFocus = null) {
    document.removeEventListener("focusin", this.trapFocusHandlers.focusin);
    document.removeEventListener("focusout", this.trapFocusHandlers.focusout);
    document.removeEventListener("keydown", this.trapFocusHandlers.keydown);

    if (elementToFocus) elementToFocus.focus();
  }

  trapFocus(container, elementToFocus = container) {
    var elements = getFocusableElements(container);
    var first = elements[0];
    var last = elements[elements.length - 1];

    this.removeTrapFocus();

    this.trapFocusHandlers.focusin = (event) => {
      if (
        event.target !== container &&
        event.target !== last &&
        event.target !== first
      )
        return;

      document.addEventListener("keydown", this.trapFocusHandlers.keydown);
    };

    this.trapFocusHandlers.focusout = () => {
      document.removeEventListener("keydown", this.trapFocusHandlers.keydown);
    };

    this.trapFocusHandlers.keydown = (event) => {
      if (event.code.toUpperCase() !== "TAB") return;
      // On the last focusable element and tab forward, focus the first element.
      if (event.target === last && !event.shiftKey) {
        event.preventDefault();
        first.focus();
      }

      //  On the first focusable element and tab backward, focus the last element.
      if (
        (event.target === container || event.target === first) &&
        event.shiftKey
      ) {
        event.preventDefault();
        last.focus();
      }
    };

    document.addEventListener("focusout", this.trapFocusHandlers.focusout);
    document.addEventListener("focusin", this.trapFocusHandlers.focusin);

    elementToFocus.focus();

    if (
      elementToFocus.tagName === "INPUT" &&
      ["search", "text", "email", "url"].includes(elementToFocus.type) &&
      elementToFocus.value
    ) {
      elementToFocus.setSelectionRange(0, elementToFocus.value.length);
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;

    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);
    this.classList.add("animate", "active");

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") ||
          this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add("overflow-hidden");

    releaseQuickShopScrollLock();
    // Fallback: if a lock is still live from somewhere else, tell ScrollLock that
    // touches inside the drawer are allowed.
    this.classList.add("allow-scroll-while-locked");
  }

  close() {
    this.classList.remove("active");
    this.classList.remove("allow-scroll-while-locked");
    document.body.classList.remove("overflow-hidden");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open")
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector(".drawer__inner").classList.contains("is-empty") &&
      this.querySelector(".drawer__inner").classList.remove("is-empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    this.querySelector("#CartDrawer-Overlay").addEventListener(
      "click",
      this.close.bind(this)
    );
    this.open();
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "HeaderCartIcons",
        section: "header-cart-icons",
        selector: "cart-drawer-opener",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "HeaderCartIcons",
        section: "header-cart-icons",
        selector: "cart-drawer-opener",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);

// QuantityInput class removed - using quantity-input custom element from quantity-input.js instead
